import {
  NAVIGATION_VIEW_SWITCH_USER_ID,
  NavigationView,
  SYSADMIN_USER_IDS,
  normalizeNavigationView,
} from '@/shared/constants/navigation-view.enum';
import type { NavigationPermissions } from './navigation-permissions.utils';

export type NavigationProfileFlags = {
  isCorregedoria: boolean;
  ehDefensorDiretor: boolean;
};

export function isSysAdminUserId(userId: number | null | undefined): boolean {
  if (userId == null || !Number.isFinite(userId)) return false;
  return (SYSADMIN_USER_IDS as readonly number[]).includes(userId);
}

export function canSwitchNavigationView(userId: number | null | undefined): boolean {
  return userId === NAVIGATION_VIEW_SWITCH_USER_ID;
}

/** Visão efetiva retornada pela API (`GET /auth/me`). */
export function navigationViewFromApiVisao(
  visao: number | null | undefined,
): NavigationView | null {
  return normalizeNavigationView(visao);
}

/** Visão de menu: defensor com lotação de diretor regional vigente. */
export function applyDefensorDiretorView(
  view: NavigationView | null,
  ehDefensorDiretor: boolean,
): NavigationView | null {
  if (view === NavigationView.Defensor && ehDefensorDiretor) {
    return NavigationView.DefensorDiretor;
  }
  return view;
}

export function inferNavigationView(
  permissions: NavigationPermissions,
  profile: NavigationProfileFlags,
  userId: number | null | undefined,
): NavigationView {
  if (isSysAdminUserId(userId)) {
    return NavigationView.Administrador;
  }

  if (
    permissions.pessoas &&
    permissions.defensores &&
    permissions.estagiarios &&
    permissions.colaboradores &&
    !permissions.atuacao &&
    !permissions.comarca &&
    !permissions.usuarios &&
    !permissions.lotacoes &&
    !permissions.movimentos
  ) {
    return NavigationView.RecursosHumanos;
  }

  if (
    permissions.pessoas &&
    permissions.atuacao &&
    permissions.defensoria &&
    permissions.comarca &&
    !permissions.lotacoes &&
    !permissions.movimentos &&
    !permissions.usuarios
  ) {
    return NavigationView.Auxiliar;
  }

  if (permissions.usuarios || permissions.logs) {
    return NavigationView.Administrador;
  }

  if (profile.isCorregedoria && !profile.ehDefensorDiretor) {
    return NavigationView.Corregedoria;
  }

  if (profile.ehDefensorDiretor && permissions.defensores) {
    return NavigationView.DefensorDiretor;
  }

  /** Gestão de estagiários/folhas — cadastro de pessoas, sem movimentações nem admin. */
  if (
    permissions.estagiarios &&
    permissions.folhas &&
    permissions.pessoas &&
    permissions.lotacoes &&
    !permissions.movimentos &&
    !permissions.logs
  ) {
    return NavigationView.Subcorregedoria;
  }

  if (permissions.colaboradores && permissions.folhas) {
    return NavigationView.Colaborador;
  }

  if (permissions.defensores || permissions.relatoriosAtividades) {
    return NavigationView.Defensor;
  }

  if (permissions.folhas) {
    return NavigationView.Estagiario;
  }

  if (permissions.movimentos || permissions.lotacoes) {
    return NavigationView.Corregedoria;
  }

  return NavigationView.Administrador;
}

export function resolveEffectiveNavigationView(
  inferred: NavigationView,
  override: NavigationView | null | undefined,
  userId: number | null | undefined,
  options?: { usarOverrideSessao?: boolean },
): NavigationView {
  const overrideValido = normalizeNavigationView(override);

  if (
    overrideValido != null &&
    (canSwitchNavigationView(userId) || options?.usarOverrideSessao)
  ) {
    return overrideValido;
  }
  return inferred;
}
